import {
  CalendarIcon,
  HomeIcon,
  ListBulletIcon,
  QrCodeIcon,
  RectangleGroupIcon,
  TruckIcon,
  UsersIcon,
} from "@heroicons/react/24/outline";
import { Fragment } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useLayoutStore } from "../../context";
import { classNames } from "../../utils/helpers";

/** TCSB admin navigation items (BLU-004 §6.1) */
const NAVIGATION_ITEMS = [
  { name: "Dashboard", icon: <HomeIcon />, href: "/admin", isExact: true },
  { name: "Menu",      icon: <ListBulletIcon />, href: "/admin/menu" },
  { name: "Categories", icon: <RectangleGroupIcon />, href: "/admin/categories" },
  { name: "Vehicles",  icon: <TruckIcon />, href: "/admin/vehicles" },
  { name: "Events",    icon: <CalendarIcon />, href: "/admin/events" },
  { name: "Users",     icon: <UsersIcon />, href: "/admin/users" },
  { name: "App / Share", icon: <QrCodeIcon />, href: "/admin/app-share" },
];

/** Single sidebar nav item with TCSB theming. */
const NavItem = ({ item }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarMenuOpen] = useLayoutStore((state) => [state.sidebarMenuOpen]);

  const isActive = item.isExact
    ? location.pathname === item.href
    : location.pathname.startsWith(item.href);

  return (
    <button
      onClick={() => navigate(item.href)}
      aria-label={item.name}
      className={classNames(
        "group flex items-center p-4 h-10 text-sm font-heading tracking-wider relative w-full transition-colors",
        isActive
          ? "text-teal bg-teal/10"
          : "text-charcoal/60 hover:text-teal hover:bg-teal/5"
      )}
    >
      {/* Active indicator */}
      {isActive && (
        <div className="absolute left-0 top-0 h-full w-1 bg-teal rounded-r" />
      )}
      {/* Icon */}
      <div
        className={classNames(
          "ml-2 mr-3 flex-shrink-0 h-5 w-5",
          isActive ? "text-teal" : "text-charcoal/40 group-hover:text-teal"
        )}
        aria-hidden="true"
      >
        {item.icon}
      </div>
      {/* Label (only when sidebar is expanded) */}
      {sidebarMenuOpen && (
        <span className="whitespace-nowrap">{item.name}</span>
      )}
    </button>
  );
};

/**
 * SideNavMenu — TCSB admin sidebar navigation.
 * Collapses to icon-only when sidebarMenuOpen is false.
 */
export default function SideNavMenu() {
  const [sidebarMenuOpen] = useLayoutStore((state) => [state.sidebarMenuOpen]);

  return (
    <Fragment>
      <div
        className={classNames(
          sidebarMenuOpen ? "w-56" : "w-16",
          "fixed top-14 flex min-h-0 flex-1 flex-col border-r border-aluminum/30 bg-cream h-[calc(100vh-3.5rem)] shadow-sm z-40 transform duration-200"
        )}
      >
        <div className="flex flex-1 flex-col overflow-y-auto overflow-x-hidden pt-2">
          <nav className="flex-1" aria-label="Admin sidebar">
            {NAVIGATION_ITEMS.map((item) => (
              <NavItem key={item.href} item={item} />
            ))}
          </nav>
        </div>
      </div>
    </Fragment>
  );
}
